import { useState } from 'react';
import { useCartStore, seatsLabel } from '../store/cartStore.js';
import { IcoClose } from './common/Icons.jsx';
import { usarPlan } from '../lib/plan.js';
import styles from './ItemEditSheet.module.css';
import { useAtrasCierra } from '../lib/atras.js';

const QUICK_DISCOUNTS = [
  { label: '10%', pct: 10 },
  { label: '15%', pct: 15 },
  { label: '20%', pct: 20 },
  { label: '25%', pct: 25 },
  { label: 'Gratis', pct: 100 },
];

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

// Lo que más se pide en una mesa, a un toque. Se combinan: "Sin cebolla, bien
// cocido" son dos toques. Lo raro se escribe.
const NOTAS_RAPIDAS = ['Sin cebolla', 'Sin cilantro', 'Sin picante', 'Bien cocido', 'Término medio', 'Para llevar', 'Alergia'];
const NOTA_MAX = 60;

// `puedeCobrar` false = comandera: el mesero cambia cantidad y dueño, pero el
// precio y el descuento son decisiones de dinero que toma caja al cerrar.
export default function ItemEditSheet({ item, comensales = [], onCompartir = null, puedeCobrar = true, onClose }) {
  useAtrasCierra(true, onClose);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const setItemPrice   = useCartStore((s) => s.setItemPrice);
  const setItemNote    = useCartStore((s) => s.setItemNote);
  const removeItem     = useCartStore((s) => s.removeItem);

  // Una cuenta guardada puede traer el producto sin `price` (solo id y
  // nombre): el precio base de la línea es lo que sí viaja siempre.
  const originalPrice = Number(item.product?.price ?? item.base_price ?? item.unit_price) || 0;

  const { cocina } = usarPlan();
  const [qty,   setQty]   = useState(String(item.quantity));
  const [price, setPrice] = useState(String(item.unit_price));
  const [nota,  setNota]  = useState(item.note ?? '');

  // Un atajo que ya está en la nota la quita; uno que no, se agrega.
  const alternarNota = (atajo) => {
    const partes = nota.split(',').map((x) => x.trim()).filter(Boolean);
    const i = partes.findIndex((x) => x.toLowerCase() === atajo.toLowerCase());
    const siguientes = i >= 0 ? partes.filter((_, k) => k !== i) : [...partes, atajo];
    setNota(siguientes.join(', ').slice(0, NOTA_MAX));
  };
  const notaTiene = (atajo) => nota.split(',').some((x) => x.trim().toLowerCase() === atajo.toLowerCase());

  const qtyNum   = Math.max(1, Number(qty)   || 1);
  const priceNum = Math.max(0, Number(price) || 0);

  const discountPct = originalPrice > 0
    ? Math.round(((originalPrice - priceNum) / originalPrice) * 100)
    : 0;

  const applyQuickDiscount = (pct) => {
    const newPrice = pct === 100 ? 0 : +(originalPrice * (1 - pct / 100)).toFixed(2);
    setPrice(String(newPrice));
  };

  const handleConfirm = () => {
    if (qtyNum !== item.quantity)       updateQuantity(item.lineId, qtyNum);
    if (priceNum !== item.unit_price)   setItemPrice(item.lineId, priceNum);
    // Al final: la nota cambia el `lineId`, y lo de arriba lo usa.
    if (nota.trim() !== (item.note ?? '')) setItemNote(item.lineId, nota);
    onClose();
  };

  const handleRemove = () => {
    removeItem(item.lineId);
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.sheet}>
        <div className={styles.handle} />

        <div className={styles.header}>
          <div className={styles.itemName}>{item.product.name}</div>
          <div className={styles.itemOriginal}>Precio lista: {fmt(originalPrice)}</div>
        </div>

        {/* De quién es el platillo.
            Es el camino VISIBLE para compartirlo entre varios: el otro es
            dejar el dedo en el chip de la línea, que funciona pero que nadie
            adivina —el chip no anuncia el gesto y esta hoja, que es donde el
            mesero entra al tocar el nombre, no decía nada de comensales—. Solo
            aparece en restaurante con gente sentada. */}
        {onCompartir && (
          <div className={styles.field}>
            <label className={styles.label}>¿De quién es?</label>
            <button className={styles.duenio} onClick={() => onCompartir(item)}>
              <span className={styles.duenioAhora}>
                {item.seats?.length ? seatsLabel(item.seats, comensales) : 'De la mesa'}
              </span>
              <span className={styles.duenioAccion}>Cambiar o compartir ›</span>
            </button>
          </div>
        )}

        {/* Cantidad */}
        <div className={styles.field}>
          <label className={styles.label}>Cantidad</label>
          <div className={styles.qtyRow}>
            <button className={styles.qtyBtn} onClick={() => setQty(String(Math.max(1, qtyNum - 1)))}>−</button>
            <input
              className={`input ${styles.qtyInput}`}
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputMode="numeric"
            />
            <button className={styles.qtyBtn} onClick={() => setQty(String(qtyNum + 1))}>+</button>
          </div>
        </div>

        {/* Nota para cocina. Por línea: la nota general de la venta no sabe
            decir cuál de los tres tacos va sin cilantro. */}
        <div className={styles.field}>
          <label className={styles.label} htmlFor="nota-cocina">{cocina ? 'Nota para cocina' : 'Nota'}</label>
          <div className={styles.quickRow}>
            {NOTAS_RAPIDAS.map((a) => (
              <button
                key={a}
                type="button"
                className={`${styles.quickBtn} ${notaTiene(a) ? styles.quickBtnActive : ''}`}
                onClick={() => alternarNota(a)}
                aria-pressed={notaTiene(a)}
              >
                {a}
              </button>
            ))}
          </div>
          <input
            id="nota-cocina"
            className="input"
            value={nota}
            maxLength={NOTA_MAX}
            placeholder="Sin cebolla, término medio…"
            onChange={(e) => setNota(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
          />
        </div>

        {/* Precio unitario */}
        {puedeCobrar && (
        <div className={styles.field}>
          <div className={styles.priceLabelRow}>
            <label className={styles.label}>Precio unitario</label>
            {discountPct > 0 && (
              <span className={styles.discountBadge}>−{discountPct}%</span>
            )}
          </div>
          <div className={styles.currencyRow}>
            <span className={styles.currencySymbol}>$</span>
            <input
              className={`input ${styles.priceInput}`}
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
              style={{ paddingLeft: 32 }}
            />
          </div>
        </div>

        )}

        {/* Descuentos rápidos */}
        {puedeCobrar && (
        <div className={styles.quickRow}>
          {QUICK_DISCOUNTS.map((d) => (
            <button
              key={d.label}
              className={`${styles.quickBtn} ${discountPct === d.pct ? styles.quickBtnActive : ''}`}
              onClick={() => applyQuickDiscount(d.pct)}
            >
              {d.label}
            </button>
          ))}
          {discountPct > 0 && (
            <button className={styles.quickBtn} onClick={() => setPrice(String(originalPrice))}>
              <IcoClose size={12} /> Reset
            </button>
          )}
        </div>
        )}

        {/* Línea de total */}
        <div className={styles.totalLine}>
          <span>Subtotal de este ítem</span>
          <strong>{fmt(qtyNum * priceNum)}</strong>
        </div>

        <div className={styles.actions}>
          <button className="btn btn--danger" onClick={handleRemove}>
            Quitar
          </button>
          <button className="btn btn--primary" style={{ flex: 1 }} onClick={handleConfirm}>
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
